import { titleFromSnakeOrCamel } from "@/lib/string";
import {
  HashtagIcon,
  AtSymbolIcon,
  ChatAltIcon,
  CalendarIcon,
  ClockIcon,
  StopIcon,
  UserIcon,
  CalculatorIcon,
  FlagIcon,
} from "@heroicons/react/outline";
import _ from "lodash";
import React from "react";
import { DescriptionTerm, DescriptionDetails, DescriptionDetailsTruncated, DescriptionList } from "../DescriptionList";
import { InlineLink } from "@components/library/InlineLink";
import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import { CurrencyDollar } from "@phosphor-icons/react";
import Tooltip from "@components/library/Tooltip";
import { getFileFromLog, Log } from "@/types/app/log";
import { getFileHref } from "@/lib/path-utils";

interface Props {
  log: Log;
}

const MetadataDetails = ({ log }: Props) => {
  const { slug } = useActiveOrganization();
  const file = getFileFromLog(log);

  return (
    <DescriptionList>
      <DescriptionTerm>
        <HashtagIcon className="mr-8 h-16 w-16 text-icon-base-2" />
        ID
      </DescriptionTerm>
      <DescriptionDetails>
        <InlineLink
          className="inline-flex gap-4"
          href={`${getFileHref({ slug, id: file.id, page: "logs" })}?id=${log.id}`}
        >
          {log.id}
        </InlineLink>
      </DescriptionDetails>

      <DescriptionTerm>
        <CalendarIcon className="mr-8 h-16 w-16 text-icon-base-2" />
        Timestamp
      </DescriptionTerm>
      <DescriptionDetails>{log.created_at.toISOString()}</DescriptionDetails>

      <DescriptionTerm>
        <AtSymbolIcon className="mr-8 h-16 w-16 text-icon-base-2" />
        Source
      </DescriptionTerm>
      <DescriptionDetails>{log.source || <span className="italic text-icon-base-2">None</span>}</DescriptionDetails>

      <DescriptionTerm>
        <UserIcon className="mr-8 h-16 w-16 text-icon-base-2" />
        User ID
      </DescriptionTerm>
      <DescriptionDetails>{log.user || <span className="italic text-icon-base-2">None</span>}</DescriptionDetails>

      {/* Latency might be 0 which leads to React rendering the first predicate */}
      {log.provider_latency !== null && (
        <>
          <DescriptionTerm>
            <ClockIcon className="mr-8 h-16 w-16 text-icon-base-2" />
            Latency
          </DescriptionTerm>
          <DescriptionDetails>{(log.provider_latency * 1000).toFixed(0)} ms</DescriptionDetails>
        </>
      )}
      {"prompt_tokens" in log && log.prompt_tokens !== null && "output_tokens" in log && log.output_tokens !== null && (
        <>
          <DescriptionTerm>
            <HashtagIcon className="mr-8 h-16 w-16 text-icon-base-2" />
            Tokens
          </DescriptionTerm>
          <DescriptionDetails className="tabular-nums">
            <Tooltip
              content={
                <span>
                  {log.prompt_tokens} in prompt, {log.output_tokens} in output
                </span>
              }
            >
              <span>{log.prompt_tokens + log.output_tokens} tokens</span>
            </Tooltip>
          </DescriptionDetails>
        </>
      )}
      {"prompt_cost" in log && log.prompt_cost !== null && log.output_cost !== null && (
        <>
          <DescriptionTerm>
            <CurrencyDollar weight="bold" className="mr-8 h-16 w-16 text-icon-base-2" />
            Cost
          </DescriptionTerm>
          <DescriptionDetails className="tabular-nums">
            <Tooltip
              content={
                <span>
                  $ {log.prompt_cost.toFixed(5)} prompt, $ {log.output_cost.toFixed(5)} output
                </span>
              }
            >
              <span>$ {(log.prompt_cost + log.output_cost).toFixed(5)}</span>
            </Tooltip>
          </DescriptionDetails>
        </>
      )}

      <DescriptionTerm>
        <CalculatorIcon className="mr-8 h-16 w-16 text-icon-base-2" />
        Tool choice
      </DescriptionTerm>
      {"tool_choice" in log ? (
        <DescriptionDetails>
          {log.tool_choice === null ? (
            <span className="italic text-icon-base-2">None</span>
          ) : typeof log.tool_choice === "object" ? (
            JSON.stringify(log.tool_choice, null, 2)
          ) : (
            log.tool_choice
          )}
        </DescriptionDetails>
      ) : (
        <DescriptionDetails>None</DescriptionDetails>
      )}

      {"finish_reason" in log && log.finish_reason && (
        <>
          <DescriptionTerm>
            <StopIcon className="mr-8 h-16 w-16 text-icon-base-2" />
            Finish reason
          </DescriptionTerm>
          <DescriptionDetails>{log.finish_reason}</DescriptionDetails>
        </>
      )}

      {log.session_id && (
        <>
          <DescriptionTerm>
            <HashtagIcon className="mr-8 h-16 w-16 text-icon-base-2" />
            Session ID
          </DescriptionTerm>
          <DescriptionDetails>
            <InlineLink
              className="inline-flex gap-4"
              href={`${getFileHref({ slug, id: file.id, page: "sessions" })}/${log.session_id}?datumId=${log.id}`}
            >
              {log.session_id}
            </InlineLink>
          </DescriptionDetails>
        </>
      )}
      {log.save !== null && (
        <>
          <DescriptionTerm>
            <FlagIcon className="mr-8 h-16 w-16 text-icon-base-2" />
            Save flag
          </DescriptionTerm>
          <DescriptionDetails>{JSON.stringify(log.save)}</DescriptionDetails>
        </>
      )}

      {(log.metadata !== null ? Object.entries(log.metadata) : []).map(([name, value]) => (
        <React.Fragment key={name}>
          <DescriptionTerm className="truncate">
            <ChatAltIcon className="mr-8 h-16 w-16 shrink-0 text-icon-base-2" />
            <div className="truncate">{titleFromSnakeOrCamel(name)}</div>
          </DescriptionTerm>
          <DescriptionDetailsTruncated>
            {_.isObject(value) ? (
              <span className="whitespace-pre-wrap">{JSON.stringify(value, undefined, 2)}</span>
            ) : (
              value
            )}
          </DescriptionDetailsTruncated>
        </React.Fragment>
      ))}
    </DescriptionList>
  );
};

export default MetadataDetails;
